import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { EmailInputWithSuggestion } from '@/components/common/EmailInputWithSuggestion';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, Mail, Phone, MapPin, Globe, Save, Loader2, Pencil, X, Wrench, Sparkles, Hammer, CheckCircle2, XCircle, Package, ImageIcon, Upload, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRef } from 'react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  website: string;
}

interface ServiceData {
  offers_cleaning: boolean;
  offers_repairs: boolean;
}

interface ShippingData {
  useDifferentAddress: boolean;
  street_address: string;
  city: string;
  state: string;
  zip: string;
}

const PortalSettings = () => {
  const navigate = useNavigate();
  const { company, isOwner, isLoading: authLoading, hasPermission } = usePortalAuth();
  const { isReadOnly } = useReadOnly();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    website: '',
  });
  const [originalData, setOriginalData] = useState<FormData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  // Services state
  const [serviceData, setServiceData] = useState<ServiceData>({
    offers_cleaning: false,
    offers_repairs: false,
  });

  // Shipping address state
  const [shippingData, setShippingData] = useState<ShippingData>({
    useDifferentAddress: false,
    street_address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [originalShippingData, setOriginalShippingData] = useState<ShippingData | null>(null);
  const [isEditingShipping, setIsEditingShipping] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);

  // Logo state
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Redirect users without manage_settings permission (owners always have access)
  useEffect(() => {
    if (!authLoading && !isOwner && !hasPermission('manage_settings')) {
      toast.error('You do not have permission to access settings');
      navigate('/portal/dashboard');
    }
  }, [authLoading, isOwner, hasPermission, navigate]);

  // Load company data
  useEffect(() => {
    if (company) {
      // Use separate address columns directly
      const data: FormData = {
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.street_address || '',
        city: company.city || '',
        state: company.state || '',
        zip: company.zip || '',
        website: company.website || '',
      };
      setFormData(data);
      setOriginalData(data);

      // Load service data
      const services: ServiceData = {
        offers_cleaning: company.offers_cleaning || false,
        offers_repairs: company.offers_repairs || false,
      };
      setServiceData(services);

      // Load shipping data
      const shipping: ShippingData = {
        useDifferentAddress: company.use_different_shipping_address || false,
        street_address: company.shipping_street_address || '',
        city: company.shipping_city || '',
        state: company.shipping_state || '',
        zip: company.shipping_zip || '',
      };
      setShippingData(shipping);
      setOriginalShippingData(shipping);

      // Load logo
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setLogoUrl((company as any).logo_url ?? null);
    }
  }, [company]);

  const hasChanges = () => {
    if (!originalData) return false;
    return Object.keys(formData).some(
      (key) => formData[key as keyof FormData] !== originalData[key as keyof FormData]
    );
  };


  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !company) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop();
      const filePath = `logos/${company.id}/${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('monument-photos')
        .upload(filePath, file, { contentType: file.type, upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('monument-photos')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('monument_companies')
        .update({ logo_url: publicUrl })
        .eq('id', company.id);

      if (updateError) throw updateError;

      setLogoUrl(publicUrl);
      toast.success('Logo updated');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!company) return;
    setIsUploadingLogo(true);
    try {
      const { error } = await supabase
        .from('monument_companies')
        .update({ logo_url: null })
        .eq('id', company.id);
      if (error) throw error;
      setLogoUrl(null);
      toast.success('Logo removed');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Company name must be less than 100 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^[\d\s\-+()]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData.address && formData.address.length > 200) {
      newErrors.address = 'Address must be less than 200 characters';
    }

    if (formData.website && formData.website.trim()) {
      if (!/^https?:\/\/.+/.test(formData.website)) {
        newErrors.website = 'Website must start with http:// or https://';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !company) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('monument_companies')
        .update({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
          website: formData.website.trim() || null,
        })
        .eq('id', company.id);

      if (error) throw error;

      // Sync non-address changes to linked fulfillment partner if one exists
      const linkedPartnerId = company.linked_fulfillment_partner_id;
      if (linkedPartnerId) {
        await supabase
          .from('fulfillment_partners')
          .update({
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim() || null,
          })
          .eq('id', linkedPartnerId);
      }

      setOriginalData({ ...formData });
      setIsEditing(false);
      toast.success('Settings saved successfully');
    } catch (error: unknown) {
      console.error('Error saving settings:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };


  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setFormData({ ...originalData });
    }
    setErrors({});
    setIsEditing(false);
  };


  // Shipping address handlers
  const hasShippingChanges = () => {
    if (!originalShippingData) return false;
    return (
      shippingData.useDifferentAddress !== originalShippingData.useDifferentAddress ||
      shippingData.street_address !== originalShippingData.street_address ||
      shippingData.city !== originalShippingData.city ||
      shippingData.state !== originalShippingData.state ||
      shippingData.zip !== originalShippingData.zip
    );
  };

  const handleSaveShipping = async () => {
    if (!company) return;

    setIsSavingShipping(true);
    try {
      const { error } = await supabase
        .from('monument_companies')
        .update({
          use_different_shipping_address: shippingData.useDifferentAddress,
          shipping_street_address: shippingData.useDifferentAddress ? shippingData.street_address.trim() || null : null,
          shipping_city: shippingData.useDifferentAddress ? shippingData.city.trim() || null : null,
          shipping_state: shippingData.useDifferentAddress ? shippingData.state.trim() || null : null,
          shipping_zip: shippingData.useDifferentAddress ? shippingData.zip.trim() || null : null,
        })
        .eq('id', company.id);

      if (error) throw error;

      setOriginalShippingData({ ...shippingData });
      setIsEditingShipping(false);
      toast.success('Shipping address saved successfully');
    } catch (error: unknown) {
      console.error('Error saving shipping address:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save shipping address');
    } finally {
      setIsSavingShipping(false);
    }
  };

  const handleCancelShipping = () => {
    if (originalShippingData) {
      setShippingData({ ...originalShippingData });
    }
    setIsEditingShipping(false);
  };

  const handleShippingToggle = (checked: boolean) => {
    if (checked && !shippingData.street_address) {
      // Pre-fill with business address
      setShippingData({
        useDifferentAddress: true,
        street_address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
      });
    } else {
      setShippingData(prev => ({ ...prev, useDifferentAddress: checked }));
    }
  };

  // Get the effective shipping address for display
  const getEffectiveShippingAddress = () => {
    if (shippingData.useDifferentAddress) {
      return [shippingData.street_address, shippingData.city, shippingData.state, shippingData.zip]
        .filter(Boolean)
        .join(', ') || 'Not provided';
    }
    return [formData.address, formData.city, formData.state, formData.zip]
      .filter(Boolean)
      .join(', ') || 'Not provided';
  };

  if (authLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-56" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-6 w-11 rounded-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isOwner) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your company profile and preferences
        </p>
      </div>

      {/* Company Information Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Information
            </CardTitle>
            <CardDescription>
              {isEditing ? 'Update your company details' : 'Your company details visible to your team'}
            </CardDescription>
          </div>
          {!isEditing && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isEditing ? (
            // Edit Mode
            <div className="space-y-4">
              {/* Company Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Company Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter company name"
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              {/* Contact Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Contact Email <span className="text-destructive">*</span>
                </Label>
                <EmailInputWithSuggestion
                  id="email"
                  value={formData.email}
                  onEmailChange={(email) => handleChange('email', email)}
                  placeholder="company@example.com"
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className={errors.phone ? 'border-destructive' : ''}
                />
                {errors.phone && (
                  <p className="text-sm text-destructive">{errors.phone}</p>
                )}
              </div>

              {/* Address (locked) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Address
                </Label>
                <p className="text-sm py-2 px-3 rounded-md border bg-muted/50 text-muted-foreground">
                  {[formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(', ') || 'Not provided'}
                </p>
                <p className="text-xs text-muted-foreground">
                  To update your address, please contact us at support@pontis.life
                </p>
              </div>

              {/* Website */}
              <div className="space-y-2">
                <Label htmlFor="website" className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  Website
                </Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://www.example.com"
                  className={errors.website ? 'border-destructive' : ''}
                />
                {errors.website && (
                  <p className="text-sm text-destructive">{errors.website}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges() || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="space-y-6">
              {/* Company Name */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  Company Name
                </div>
                <p className="text-base">{formData.name || 'Not provided'}</p>
              </div>

              {/* Contact Email */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  Contact Email
                </div>
                <p className="text-base">{formData.email || 'Not provided'}</p>
              </div>

              {/* Phone Number */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </div>
                <p className="text-base">{formData.phone || 'Not provided'}</p>
              </div>

              {/* Address */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  Address
                </div>
                <p className="text-base">
                  {[formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(', ') || 'Not provided'}
                </p>
              </div>

              {/* Website */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  Website
                </div>
                {formData.website ? (
                  <a
                    href={formData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-primary hover:underline"
                  >
                    {formData.website}
                  </a>
                ) : (
                  <p className="text-base">Not provided</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Logo Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Company Logo
          </CardTitle>
          <CardDescription>
            Shown on your memorials' Info tab so families can recognize your company
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Preview */}
            <div className="h-24 w-24 rounded-lg border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt="Company logo" className="h-full w-full object-contain" />
              ) : (
                <Building2 className="h-10 w-10 text-muted-foreground" />
              )}
            </div>

            {/* Actions */}
            {!isReadOnly && (
              <div className="flex flex-col gap-3">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
                <Button
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploadingLogo}
                >
                  {isUploadingLogo ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      {logoUrl ? 'Replace Logo' : 'Upload Logo'}
                    </>
                  )}
                </Button>
                {logoUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLogo}
                    disabled={isUploadingLogo}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">PNG, JPG up to 2MB</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Services Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Services
          </CardTitle>
          <CardDescription>
            Services enabled for your company. Contact the Pontis team to update your service offerings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Headstone Cleaning Status */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Headstone Cleaning</p>
                  <p className="text-sm text-muted-foreground">
                    Cleaning service for memorials
                  </p>
                </div>
              </div>
              {serviceData.offers_cleaning ? (
                <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Enabled
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <XCircle className="h-4 w-4" />
                  Not offered
                </div>
              )}
            </div>

            {/* Fix Headstone Requests Status */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <Hammer className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Fix Headstone Requests</p>
                  <p className="text-sm text-muted-foreground">
                    Allow visitors to request repairs through the memorial page
                  </p>
                </div>
              </div>
              {serviceData.offers_repairs ? (
                <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Enabled
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <XCircle className="h-4 w-4" />
                  Not offered
                </div>
              )}
            </div>

            {/* Info Note */}
            <p className="text-sm text-muted-foreground pt-2">
              When enabled, customers can request these services through their memorial page and will be connected to your team.
            </p>
            <p className="text-xs text-muted-foreground">
              To change your service offerings, contact{' '}
              <a href="mailto:support@pontis.life" className="underline">support@pontis.life</a>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Shipping Address Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Shipping Address
            </CardTitle>
            <CardDescription>
              {isEditingShipping 
                ? 'Set your default shipping address for medallion orders' 
                : 'Default shipping address for medallion orders'}
            </CardDescription>
          </div>
          {!isEditingShipping && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={() => setIsEditingShipping(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isEditingShipping ? (
            // Edit Mode - Shipping
            <div className="space-y-4">
              {/* Use Different Address Toggle */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="different-address"
                  checked={shippingData.useDifferentAddress}
                  onCheckedChange={handleShippingToggle}
                />
                <Label htmlFor="different-address" className="text-sm font-medium cursor-pointer">
                  Use a different shipping address than business address
                </Label>
              </div>

              {shippingData.useDifferentAddress ? (
                <>
                  {/* Shipping Street Address */}
                  <div className="space-y-2">
                    <Label htmlFor="shipping-address" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      Shipping Street Address
                    </Label>
                    <Input
                      id="shipping-address"
                      value={shippingData.street_address}
                      onChange={(e) => setShippingData(prev => ({ ...prev, street_address: e.target.value }))}
                      placeholder="123 Main Street"
                    />
                  </div>

                  {/* Shipping City, State, ZIP */}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="shipping-city">City</Label>
                      <Input
                        id="shipping-city"
                        value={shippingData.city}
                        onChange={(e) => setShippingData(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Springfield"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="shipping-state">State</Label>
                      <Input
                        id="shipping-state"
                        value={shippingData.state}
                        onChange={(e) => setShippingData(prev => ({ ...prev, state: e.target.value }))}
                        placeholder="IL"
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="shipping-zip">ZIP Code</Label>
                      <Input
                        id="shipping-zip"
                        value={shippingData.zip}
                        onChange={(e) => setShippingData(prev => ({ ...prev, zip: e.target.value }))}
                        placeholder="62701"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="rounded-lg border p-4 bg-muted/50">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span>Medallions will ship to your business address:</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 ml-6">
                    {[formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(', ') || 'No business address set'}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleCancelShipping}
                  disabled={isSavingShipping}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveShipping}
                  disabled={!hasShippingChanges() || isSavingShipping}
                >
                  {isSavingShipping ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // View Mode - Shipping
            <div className="space-y-4">
              <div className="rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">
                        {shippingData.useDifferentAddress ? 'Custom Shipping Address' : 'Business Address'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {getEffectiveShippingAddress()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    Active
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                This address will be pre-filled when ordering medallions. You can still modify it during checkout.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalSettings;
